import {
  PLAYER_DEFAULTS,
  loadMenuPack,
  usableMenuPack,
  type HeartbeatInput,
  type MenuPack,
  type PlayerCommand,
  type PlayerManifest,
  type PlayerSettings,
  type SyncState,
} from '@signage/shared';
import type {
  ClockAdapter,
  DatabaseAdapter,
  FileSystemAdapter,
  LoggerAdapter,
  PlayerEventRecord,
  ProofOfPlayRecord,
} from './adapters.js';
import { ApiError, type DeviceDescriptor, type PlayerApiClient } from './api-client.js';
import { SyncEngine } from './sync-engine.js';
import { buildPlaybackPlan, planSignature, type PlaybackPlan } from './playback-planner.js';
import { OFFLINE_CHECK_INTERVAL_MS, shouldShowOfflineNotice } from './offline-notice.js';

export interface RuntimeHostInfo {
  device: DeviceDescriptor;
  storage: () => Promise<{ totalBytes: number; freeBytes: number }>;
  /**
   * Show a notice on screen once the player has been out of contact with the server this long
   * (see offline-notice.ts; normally `OFFLINE_NOTICE_AFTER_MS`). Omitted: never shown. Only the
   * Android player sets it.
   */
  offlineNoticeAfterMs?: number;
  /**
   * A clock that only moves forward and no clock change affects, in ms. Defaults to
   * `performance.now()`. Used to count running time while out of contact.
   */
  monotonicNow?: () => number;
}

export interface RuntimeCallbacks {
  onPlanChanged?: (plan: PlaybackPlan) => void;
  onStatusChanged?: (status: RuntimeStatus) => void;
  onCommand?: (command: PlayerCommand) => Promise<'done' | 'failed'> | 'done' | 'failed';
  onUnpaired?: () => void;
}

export interface RuntimeStatus {
  online: boolean;
  paired: boolean;
  syncState: SyncState;
  activeContentVersion: string | null;
  serverContentVersion: string | null;
  lastHeartbeatAt: string | null;
  lastSyncAt: string | null;
  /** The last successful contact with the server (heartbeat, sync or pairing), persisted. */
  lastContactAt: string | null;
  /** True while the long-offline notice should be on screen (hosts that enable it only). */
  offlineNotice: boolean;
  lastError: string | null;
  cachedBytes: number;
  pendingProofOfPlay: number;
}

const SETTINGS_KEY = 'playerSettings';
const LAST_SYNC_KEY = 'lastSuccessfulSyncAt';
const LAST_CONTACT_KEY = 'lastServerContactAt';
const OFFLINE_RUNNING_KEY = 'offlineRunningMs';
/**
 * Contact time and running time are written to storage at most this often (but always on the
 * first contact after a gap). Each write rewrites the database file on Android; losing up to this
 * much on a power cut is nothing against a 90-day limit.
 */
const CONTACT_PERSIST_EVERY_MS = 10 * 60_000;

/**
 * The player runtime.
 *
 * Owns the periodic loops (heartbeat, content check, proof-of-play flush), keeps the current
 * playback plan up to date from local data, and executes server commands. Everything it needs
 * to keep playing lives on the device: if every network call fails forever, playback and
 * scheduling continue unchanged.
 */
export class PlayerRuntime {
  readonly sync: SyncEngine;
  private settings: PlayerSettings = { ...PLAYER_DEFAULTS };
  private plan: PlaybackPlan | null = null;
  private planSig = '';
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private contentTimer: ReturnType<typeof setInterval> | null = null;
  private popTimer: ReturnType<typeof setInterval> | null = null;
  private planTimer: ReturnType<typeof setTimeout> | null = null;
  private planTicker: ReturnType<typeof setInterval> | null = null;
  private offlineTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = true;
  /** The downloaded menu pack in use; null means the designs built into this player. */
  private menuPack: { pack: MenuPack; sha256: string; source: string } | null = null;
  private menuPackLoading: Promise<boolean> | null = null;
  /** Packs that failed to load, so a broken one is not retried on every plan tick. */
  private readonly failedMenuPacks = new Set<string>();
  /**
   * When this player process came up, by the clock adapter.
   *
   * Not `process.uptime()`: there is no `process` in a WebView, and referencing it threw out of
   * the heartbeat before the request was ever made — silently, because every caller swallows
   * heartbeat errors. The screen simply never reported in. Player uptime is also the more
   * useful number, since it survives being asked by a host that is not a Node process.
   */
  private readonly startedAtMs: number;
  private consecutiveHeartbeatFailures = 0;

  /* Long-offline notice (see offline-notice.ts). */
  private lastContactMs: number | null = null;
  private persistedContactMs: number | null = null;
  /** Running time since the last contact, by the monotonic clock. */
  private offlineRunningMs = 0;
  private persistedRunningMs = 0;
  private lastOfflineTickMono: number | null = null;

  private status: RuntimeStatus = {
    online: false,
    paired: false,
    syncState: 'idle',
    activeContentVersion: null,
    serverContentVersion: null,
    lastHeartbeatAt: null,
    lastSyncAt: null,
    lastContactAt: null,
    offlineNotice: false,
    lastError: null,
    cachedBytes: 0,
    pendingProofOfPlay: 0,
  };

  /** Reported in heartbeats so the dashboard can show what is on screen. */
  private currentPlayback: {
    playlistId: string | null;
    mediaId: string | null;
    scheduleId: string | null;
  } = { playlistId: null, mediaId: null, scheduleId: null };

  constructor(
    private readonly fs: FileSystemAdapter,
    private readonly db: DatabaseAdapter,
    private readonly api: PlayerApiClient,
    private readonly clock: ClockAdapter,
    private readonly logger: LoggerAdapter,
    private readonly host: RuntimeHostInfo,
    private readonly callbacks: RuntimeCallbacks = {},
    cacheDir?: string,
  ) {
    this.startedAtMs = clock.now();
    const dir = cacheDir ?? fs.join('.', 'media');
    this.sync = new SyncEngine(fs, db, api, clock, logger, {
      cacheDir: dir,
      playerVersion: host.device.playerVersion,
      onProgress: (progress) => {
        this.status.syncState = progress.state;
        if (progress.state === 'failed') this.status.lastError = progress.message ?? null;
        this.publishStatus();
      },
      onActivated: (manifest) => {
        this.applySettings(manifest.settings);
        this.db.setSetting(LAST_SYNC_KEY, new Date(this.clock.now()).toISOString());
        this.status.lastSyncAt = this.db.getSetting(LAST_SYNC_KEY);
        this.recordContact();
        // Load new designs before cutting the new plan, so boards appear in them straight away.
        void this.ensureMenuPack(manifest).finally(() => this.refreshPlan('content activated'));
      },
    });

    const stored = this.db.getSetting(SETTINGS_KEY);
    if (stored) {
      try {
        this.settings = { ...PLAYER_DEFAULTS, ...(JSON.parse(stored) as PlayerSettings) };
      } catch {
        /* fall back to defaults */
      }
    }
  }

  get currentPlan(): PlaybackPlan | null {
    return this.plan;
  }

  get currentStatus(): RuntimeStatus {
    return { ...this.status };
  }

  get playerSettings(): PlayerSettings {
    return { ...this.settings };
  }

  private applySettings(settings: PlayerSettings): void {
    const changed =
      settings.heartbeatIntervalSec !== this.settings.heartbeatIntervalSec ||
      settings.contentCheckIntervalSec !== this.settings.contentCheckIntervalSec ||
      settings.proofOfPlayFlushIntervalSec !== this.settings.proofOfPlayFlushIntervalSec;
    this.settings = { ...PLAYER_DEFAULTS, ...settings };
    this.db.setSetting(SETTINGS_KEY, JSON.stringify(this.settings));
    if (changed && !this.stopped) {
      // Timing is remotely configurable: restart the loops with the new intervals.
      this.startLoops();
    }
  }

  private publishStatus(): void {
    this.callbacks.onStatusChanged?.({ ...this.status });
  }

  /* --------------------------------------------------------------------- boot */

  /**
   * Starts the player. Playback begins from local storage before any network call is made, so
   * a device that boots without internet still shows its content immediately.
   */
  async start(): Promise<void> {
    this.stopped = false;
    const token = this.db.getSetting('deviceToken');
    this.status.paired = Boolean(token);
    if (token) this.api.setDeviceToken(token);
    const baseUrl = this.db.getSetting('apiBaseUrl');
    if (baseUrl) this.api.setBaseUrl(baseUrl);

    const boot = await this.sync.validateOnBoot();
    this.status.activeContentVersion = this.sync.getActiveContentVersion();
    this.status.lastSyncAt = this.db.getSetting(LAST_SYNC_KEY);
    if (boot.manifest) this.applySettings(boot.manifest.settings);
    this.loadContact();
    this.checkOffline(false);

    await this.ensureMenuPack(boot.manifest ?? null);
    this.refreshPlan('startup');
    this.status.pendingProofOfPlay = this.db.countProofOfPlay();
    this.publishStatus();

    if (!this.status.paired) {
      this.logger.info('Player is not paired yet');
      return;
    }

    this.startLoops();

    // Kick off the first sync in the background: playback is already running.
    void this.checkForUpdates('startup').catch((err) =>
      this.logger.warn('Initial synchronization failed', { error: (err as Error).message }),
    );
  }

  stop(): void {
    this.stopped = true;
    for (const timer of [
      this.heartbeatTimer,
      this.contentTimer,
      this.popTimer,
      this.planTicker,
      this.offlineTimer,
    ]) {
      if (timer) clearInterval(timer);
    }
    if (this.planTimer) clearTimeout(this.planTimer);
    this.offlineTimer = null;
    this.heartbeatTimer = null;
    this.contentTimer = null;
    this.popTimer = null;
    this.planTicker = null;
    this.planTimer = null;
    this.sync.cancel();
  }

  private startLoops(): void {
    this.stop();
    this.stopped = false;

    this.heartbeatTimer = setInterval(() => {
      void this.heartbeat().catch(() => {});
    }, this.settings.heartbeatIntervalSec * 1000);

    this.contentTimer = setInterval(() => {
      void this.checkForUpdates('poll').catch(() => {});
    }, this.settings.contentCheckIntervalSec * 1000);

    this.popTimer = setInterval(() => {
      void this.flushProofOfPlay().catch(() => {});
      void this.flushEvents().catch(() => {});
    }, this.settings.proofOfPlayFlushIntervalSec * 1000);

    // Re-evaluate the schedule regularly so campaigns start/end on time while offline.
    this.planTicker = setInterval(() => this.refreshPlan('tick'), 15_000);

    const timers = [this.heartbeatTimer, this.contentTimer, this.popTimer, this.planTicker];
    if (this.host.offlineNoticeAfterMs !== undefined) {
      // Screens run for months without a restart: keep re-checking, not only at boot.
      this.offlineTimer = setInterval(() => this.checkOffline(true), OFFLINE_CHECK_INTERVAL_MS);
      timers.push(this.offlineTimer);
    }
    for (const timer of timers) {
      (timer as { unref?: () => void }).unref?.();
    }

    void this.heartbeat().catch(() => {});
  }

  /* ------------------------------------------------------- long-offline notice */

  private monotonicNow(): number {
    if (this.host.monotonicNow) return this.host.monotonicNow();
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /** Reads the persisted last contact and running time, at start. */
  private loadContact(): void {
    const parse = (value: string | null): number | null => {
      const ms = value ? Date.parse(value) : NaN;
      return Number.isFinite(ms) ? ms : null;
    };
    this.lastContactMs = parse(this.db.getSetting(LAST_CONTACT_KEY));
    this.persistedContactMs = this.lastContactMs;
    if (this.lastContactMs === null && this.status.paired) {
      // A screen paired before this was tracked: its last sync is the best record there is, or
      // failing that, now — the count starts late rather than showing the notice wrongly.
      this.lastContactMs = parse(this.db.getSetting(LAST_SYNC_KEY)) ?? this.clock.now();
      this.persistContact();
    }
    const running = Number(this.db.getSetting(OFFLINE_RUNNING_KEY));
    this.offlineRunningMs = Number.isFinite(running) && running > 0 ? running : 0;
    this.persistedRunningMs = this.offlineRunningMs;
    this.lastOfflineTickMono = this.monotonicNow();
    this.status.lastContactAt =
      this.lastContactMs === null ? null : new Date(this.lastContactMs).toISOString();
  }

  private persistContact(): void {
    if (this.lastContactMs === null) return;
    this.db.setSetting(LAST_CONTACT_KEY, new Date(this.lastContactMs).toISOString());
    this.persistedContactMs = this.lastContactMs;
  }

  /** The server answered: the count starts again and any notice goes. */
  private recordContact(): void {
    const now = this.clock.now();
    this.lastContactMs = now;
    this.status.lastContactAt = new Date(now).toISOString();
    this.offlineRunningMs = 0;
    this.lastOfflineTickMono = this.monotonicNow();
    // Written straight away after a gap (or if the clock moved backwards since the last write),
    // otherwise at most every few minutes: a heartbeat is a contact every minute.
    if (
      this.persistedContactMs === null ||
      now < this.persistedContactMs ||
      now - this.persistedContactMs >= CONTACT_PERSIST_EVERY_MS ||
      this.persistedRunningMs !== 0
    ) {
      this.persistContact();
    }
    if (this.persistedRunningMs !== 0) {
      this.db.setSetting(OFFLINE_RUNNING_KEY, '0');
      this.persistedRunningMs = 0;
    }
    this.checkOffline(true);
  }

  /**
   * Adds the running time since the last check and decides whether the notice shows. Publishes
   * a status change when the answer changes and `publish` is set.
   */
  checkOffline(publish = true): boolean {
    const afterMs = this.host.offlineNoticeAfterMs;
    const mono = this.monotonicNow();
    if (this.lastOfflineTickMono !== null && mono > this.lastOfflineTickMono) {
      this.offlineRunningMs += mono - this.lastOfflineTickMono;
    }
    this.lastOfflineTickMono = mono;
    if (this.offlineRunningMs - this.persistedRunningMs >= CONTACT_PERSIST_EVERY_MS) {
      this.db.setSetting(OFFLINE_RUNNING_KEY, String(Math.round(this.offlineRunningMs)));
      this.persistedRunningMs = this.offlineRunningMs;
    }

    const show =
      afterMs !== undefined &&
      this.status.paired &&
      shouldShowOfflineNotice(
        {
          nowMs: this.clock.now(),
          lastContactMs: this.lastContactMs,
          runningMs: this.offlineRunningMs,
        },
        afterMs,
      );
    if (show !== this.status.offlineNotice) {
      this.status.offlineNotice = show;
      this.logger[show ? 'warn' : 'info'](
        show ? 'Out of contact with the server too long; showing the offline notice' : 'Offline notice cleared',
        { lastContactAt: this.status.lastContactAt },
      );
      if (publish) this.publishStatus();
    }
    return show;
  }

  /* --------------------------------------------------------------- scheduling */

  /**
   * Recomputes what should be playing. Purely local: reads the stored manifest and the clock.
   */
  refreshPlan(reason: string): PlaybackPlan {
    const manifest = this.sync.getActiveManifest();
    if (this.wantedMenuPack(manifest) !== (this.menuPack?.sha256 ?? null) && !this.menuPackLoading) {
      // The pack changed underneath us (or was only just downloaded): load it, then cut again.
      void this.ensureMenuPack(manifest).then((changed) => changed && this.refreshPlan('menu designs loaded'));
    }
    const plan = buildPlaybackPlan(manifest, {
      menuPack: this.menuPack ?? undefined,
      resolvePath: (mediaId, sha256) => {
        const entry = this.db.getCacheEntry(sha256);
        if (!entry) return null;
        this.db.touchCacheEntry(sha256, this.clock.now());
        return this.sync.cacheManager.pathFor(sha256);
      },
    });

    const signature = planSignature(plan);
    this.plan = plan;

    if (signature !== this.planSig) {
      this.planSig = signature;
      this.logger.info('Playback plan changed', {
        reason,
        schedule: plan.scheduleName,
        items: plan.items.length,
        unavailable: plan.unavailableMediaIds.length,
      });
      this.callbacks.onPlanChanged?.(plan);
    }

    // Wake exactly at the next schedule boundary so a campaign switches on time.
    if (this.planTimer) clearTimeout(this.planTimer);
    if (plan.nextBoundary && !this.stopped) {
      const delay = new Date(plan.nextBoundary).getTime() - this.clock.now();
      if (delay > 0 && delay < 24 * 3600_000) {
        this.planTimer = setTimeout(() => this.refreshPlan('boundary'), delay + 250);
        (this.planTimer as { unref?: () => void }).unref?.();
      }
    }

    if (plan.unavailableMediaIds.length > 0) {
      // Something the schedule wants is not on disk; ask for a resync but keep playing.
      void this.checkForUpdates('missing-media').catch(() => {});
    }

    return plan;
  }

  /* ------------------------------------------------------------- menu designs */

  /** The pack the manifest asks for, or null for the built-in designs. */
  private wantedMenuPack(manifest: PlayerManifest | null): string | null {
    const sha256 = manifest ? usableMenuPack(manifest)?.sha256 ?? null : null;
    return sha256 && !this.failedMenuPacks.has(sha256) ? sha256 : null;
  }

  /**
   * Makes the menu pack in use the one the manifest names, once it is downloaded. Until then,
   * and if it cannot be run, the current pack (or the built-in designs) stays in use, so a board
   * always draws. Resolves true when the pack in use changed.
   */
  private ensureMenuPack(manifest: PlayerManifest | null): Promise<boolean> {
    if (this.menuPackLoading) return this.menuPackLoading.then(() => this.ensureMenuPack(manifest));
    const wanted = this.wantedMenuPack(manifest);
    if (wanted === (this.menuPack?.sha256 ?? null)) return Promise.resolve(false);
    if (!wanted) {
      this.menuPack = null;
      return Promise.resolve(true);
    }
    // Not downloaded yet: keep what is in use; the sync that fetches it activates a new plan.
    if (!this.db.getCacheEntry(wanted)) return Promise.resolve(false);

    this.menuPackLoading = (async () => {
      try {
        const bytes = await this.fs.readFile(this.sync.cacheManager.pathFor(wanted));
        const source = new TextDecoder().decode(bytes);
        const pack = loadMenuPack(source);
        this.menuPack = { pack, sha256: wanted, source };
        this.logger.info('Menu designs loaded', { pack: wanted.slice(0, 12), designs: pack.templateIds.length });
        return true;
      } catch (err) {
        this.failedMenuPacks.add(wanted);
        this.logger.warn('Menu designs could not be loaded; using the built-in ones', {
          error: (err as Error).message,
        });
        this.recordEvent({
          level: 'warning',
          code: 'player.menu_pack_failed',
          message: `Downloaded menu designs could not be loaded: ${(err as Error).message}`,
          context: { sha256: wanted },
        });
        return false;
      } finally {
        this.menuPackLoading = null;
      }
    })();
    return this.menuPackLoading;
  }

  setCurrentPlayback(info: {
    playlistId: string | null;
    mediaId: string | null;
    scheduleId: string | null;
  }): void {
    this.currentPlayback = info;
  }

  /* ---------------------------------------------------------------- heartbeat */

  async heartbeat(): Promise<void> {
    if (!this.status.paired) return;
    const storage = await this.host.storage().catch(() => ({ totalBytes: 0, freeBytes: 0 }));
    const cacheStats = await this.sync.cacheManager.stats().catch(() => ({
      cachedBytes: 0,
      fileCount: 0,
      freeBytes: 0,
      totalBytes: 0,
    }));
    this.status.cachedBytes = cacheStats.cachedBytes;

    const input: HeartbeatInput = {
      status: this.plan && this.plan.items.length > 0 ? 'playing' : 'idle',
      playerVersion: this.host.device.playerVersion,
      platform: this.host.device.platform,
      osVersion: this.host.device.osVersion ?? null,
      width: this.host.device.width,
      height: this.host.device.height,
      orientation: this.host.device.orientation,
      storageTotalBytes: storage.totalBytes || undefined,
      storageFreeBytes: storage.freeBytes || undefined,
      cacheBytes: cacheStats.cachedBytes,
      activeContentVersion: this.sync.getActiveContentVersion(),
      syncState: this.status.syncState,
      currentPlaylistId: this.currentPlayback.playlistId ?? null,
      currentMediaId: this.currentPlayback.mediaId ?? null,
      currentScheduleId: this.currentPlayback.scheduleId ?? null,
      uptimeSec: Math.max(0, Math.round((this.clock.now() - this.startedAtMs) / 1000)),
      errorCount: 0,
    };

    try {
      const response = await this.api.heartbeat(input);
      this.consecutiveHeartbeatFailures = 0;
      this.status.online = true;
      this.status.lastError = null;
      this.status.lastHeartbeatAt = new Date(this.clock.now()).toISOString();
      this.recordContact();
      this.status.serverContentVersion = response.contentVersion;
      if (response.settings) this.applySettings(response.settings);
      this.publishStatus();

      if (response.contentVersion !== this.sync.getActiveContentVersion()) {
        void this.checkForUpdates('heartbeat', response.contentVersion).catch(() => {});
      }

      for (const command of response.commands ?? []) {
        void this.executeCommand(command);
      }
    } catch (err) {
      this.consecutiveHeartbeatFailures += 1;
      this.status.online = false;
      const error = err as ApiError;
      this.status.lastError = error.message;
      this.publishStatus();

      if (error instanceof ApiError && error.isAuthError) {
        this.logger.error('The server rejected this device; it may have been unpaired', {
          status: error.status,
        });
        // Playback continues from cache: losing the pairing must not blank the screen.
        this.status.paired = false;
        this.status.offlineNotice = false;
        this.db.deleteSetting('deviceToken');
        this.callbacks.onUnpaired?.();
        this.stop();
      } else if (this.consecutiveHeartbeatFailures === 3) {
        this.recordEvent({
          level: 'warning',
          code: 'network.offline',
          message: `Heartbeat failed ${this.consecutiveHeartbeatFailures} times: ${error.message}`,
        });
      }
    }
  }

  /* ------------------------------------------------------------------ syncing */

  async checkForUpdates(reason: string, serverVersion?: string): Promise<void> {
    if (!this.status.paired || this.sync.isRunning) return;
    this.logger.debug('Checking for content updates', { reason });
    const result = await this.sync.sync({ serverVersion });
    this.status.activeContentVersion = this.sync.getActiveContentVersion();

    if (result.outcome === 'failed') {
      this.recordEvent({
        level: 'error',
        code: 'sync.failed',
        message: result.message ?? 'Synchronization failed',
        context: { contentVersion: result.contentVersion, errors: result.errors },
      });
    }
    this.publishStatus();
  }

  /* ---------------------------------------------------------- proof of play */

  recordProofOfPlay(entry: Omit<ProofOfPlayRecord, 'eventId'> & { eventId?: string }): void {
    const record: ProofOfPlayRecord = {
      eventId: entry.eventId ?? cryptoRandomId(),
      mediaId: entry.mediaId ?? null,
      playlistId: entry.playlistId ?? null,
      slideshowId: entry.slideshowId ?? null,
      scheduleId: entry.scheduleId ?? null,
      startedAt: entry.startedAt,
      completedAt: entry.completedAt,
      durationMs: entry.durationMs,
      completed: entry.completed,
    };
    this.db.enqueueProofOfPlay(record);
    this.status.pendingProofOfPlay = this.db.countProofOfPlay();

    // Flush early when the buffer grows, rather than waiting for the timer.
    if (this.status.pendingProofOfPlay >= this.settings.proofOfPlayBatchSize) {
      void this.flushProofOfPlay().catch(() => {});
    }
  }

  /**
   * Uploads buffered playback records. Rows are deleted only after the server accepts them, so
   * an offline device keeps them until connectivity returns (spec §22).
   */
  async flushProofOfPlay(): Promise<number> {
    if (!this.status.paired) return 0;
    let uploaded = 0;
    for (let batch = 0; batch < 10; batch += 1) {
      const events = this.db.takeProofOfPlay(this.settings.proofOfPlayBatchSize);
      if (events.length === 0) break;
      try {
        await this.api.uploadProofOfPlay(
          events.map((e) => ({
            eventId: e.eventId,
            mediaId: e.mediaId,
            playlistId: e.playlistId,
            slideshowId: e.slideshowId,
            scheduleId: e.scheduleId,
            startedAt: e.startedAt,
            completedAt: e.completedAt,
            durationMs: e.durationMs,
            completed: e.completed,
          })),
        );
        this.db.deleteProofOfPlay(events.map((e) => e.eventId));
        uploaded += events.length;
      } catch (err) {
        this.logger.debug('Proof-of-play upload deferred', { error: (err as Error).message });
        break;
      }
    }
    this.status.pendingProofOfPlay = this.db.countProofOfPlay();
    if (uploaded > 0) this.publishStatus();
    return uploaded;
  }

  recordEvent(entry: {
    level: PlayerEventRecord['level'];
    code: string;
    message: string;
    context?: Record<string, unknown>;
  }): void {
    this.db.enqueueEvent({
      eventId: cryptoRandomId(),
      level: entry.level,
      code: entry.code,
      message: entry.message.slice(0, 2000),
      context: entry.context ? JSON.stringify(entry.context) : null,
      occurredAt: new Date(this.clock.now()).toISOString(),
    });
  }

  async flushEvents(): Promise<number> {
    if (!this.status.paired) return 0;
    const events = this.db.takeEvents(100);
    if (events.length === 0) return 0;
    try {
      await this.api.uploadEvents(
        events.map((e) => ({
          eventId: e.eventId,
          level: e.level,
          code: e.code,
          message: e.message,
          context: e.context ? JSON.parse(e.context) : null,
          occurredAt: e.occurredAt,
        })),
      );
      this.db.deleteEvents(events.map((e) => e.eventId));
      return events.length;
    } catch {
      return 0;
    }
  }

  /* ----------------------------------------------------------------- commands */

  async executeCommand(command: PlayerCommand): Promise<void> {
    this.logger.info('Executing command', { command: command.command, id: command.id });
    let result: 'done' | 'failed' = 'done';
    try {
      switch (command.command) {
        case 'sync':
          await this.checkForUpdates('command');
          break;
        case 'clear_cache':
          await this.sync.clearCache();
          await this.checkForUpdates('command-clear-cache');
          break;
        default: {
          // restart / reload / screenshot / identify are host concerns.
          const handled = await this.callbacks.onCommand?.(command);
          result = handled ?? 'done';
        }
      }
    } catch (err) {
      this.logger.error('Command failed', {
        command: command.command,
        error: (err as Error).message,
      });
      result = 'failed';
    }
    await this.api.acknowledgeCommand(command.id, result).catch(() => {});
  }

  /* ------------------------------------------------------------------ pairing */

  async completePairing(params: {
    deviceToken: string;
    screenId: string;
    organizationId: string;
    apiBaseUrl?: string;
  }): Promise<void> {
    this.api.setDeviceToken(params.deviceToken);

    // The address the device paired through is known to work. Only move off it if the one the
    // server advertises also works.
    const pairedThrough = this.api.currentBaseUrl;
    const adopted = params.apiBaseUrl
      ? await this.adoptBaseUrl(params.apiBaseUrl, pairedThrough)
      : pairedThrough;

    this.db.transaction(() => {
      this.db.setSetting('deviceToken', params.deviceToken);
      this.db.setSetting('screenId', params.screenId);
      this.db.setSetting('organizationId', params.organizationId);
      this.db.setSetting('apiBaseUrl', adopted);
    });
    this.api.setBaseUrl(adopted);
    this.status.paired = true;
    this.recordContact();
    this.publishStatus();
    this.startLoops();
    await this.checkForUpdates('paired');
  }


  /**
   * Switches to a server-advertised address only if the device can actually reach it.
   *
   * `PUBLIC_API_URL` is what the server believes its public address to be, and it is routinely
   * wrong in ways the server cannot detect: a stock `localhost` default, an internal hostname
   * that does not resolve on the screen's network, or http where the device demands https.
   * Adopting it blindly overwrites the one address known to work — and because the device then
   * cannot reach the server, nothing can ever correct it remotely. Every paired screen would
   * need a site visit.
   *
   * So the new address is tried once. If it answers, it is adopted; if not, the device keeps
   * pairing through what it has and reports the problem on its next successful contact.
   */
  private async adoptBaseUrl(candidate: string, fallback: string): Promise<string> {
    const normalized = candidate.replace(/\/$/, '');
    if (normalized === fallback.replace(/\/$/, '')) return fallback;

    this.api.setBaseUrl(normalized);
    try {
      await this.api.contentVersion();
      this.logger.info('Adopted the server-advertised address', { apiBaseUrl: normalized });
      return normalized;
    } catch (err) {
      this.api.setBaseUrl(fallback);
      this.logger.warn('Server advertised an address this device cannot reach; keeping the one it paired through', {
        advertised: normalized,
        keeping: fallback,
        error: (err as Error).message,
      });
      this.recordEvent({
        level: 'warning',
        code: 'player.api_base_url_unreachable',
        message: `Could not reach the advertised address ${normalized}; still using ${fallback}`,
      });
      return fallback;
    }
  }

  getManifest(): PlayerManifest | null {
    return this.sync.getActiveManifest();
  }
}

function cryptoRandomId(): string {
  const globalCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (globalCrypto?.randomUUID) return globalCrypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
